import { useState, useRef, useEffect, useCallback } from 'react';
import { Storage } from '../storage';
import { uid, toDateKey, fromDateKey, formatDate } from '../utils';
import { nominatimSearch } from '../nominatim';
import RichTextEditor from './RichTextEditor';
import Icon from './Icon';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ─── EVENT FORM COMPONENT ─────────────────────────────────────────────────────
//
//  Used inside a Modal for both creating and editing timeline events.
//  Props:
//    initial  {object|null}  existing event data when editing; null when creating
//    onSave   {function}     receives the completed event object
//    onCancel {function}     called when user clicks Cancel
//

// Defined at module level so React sees a stable component identity across
// EventForm re-renders — prevents focus loss on every keystroke.

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const EF_SOURCE_LIMIT = 1000;
const EF_DESC_MAX_CHARS = 10000;

// Splits description HTML into plain text (for the RTE) and an array of image
// src URLs.  On edit we restore both so the thumbnail strip is not empty.
const extractDescParts = (html) => {
  if (!html) return { text: '', srcs: [] };
  const div = document.createElement('div');
  div.innerHTML = html;
  const imgs = Array.from(div.querySelectorAll('img'));
  const srcs = imgs.map(img => img.src);
  imgs.forEach(img => img.remove());
  return { text: div.innerHTML, srcs };
};

const efCountWords      = (str) => str.trim() === '' ? 0 : str.trim().split(/\s+/).length;
const efWordCountColor  = (count, limit) => {
  if (count > limit)       return 'var(--danger)';
  if (count / limit >= 0.9) return '#c9a96e';
  return 'var(--text3)';
};

const DateFields = ({ label, prefix, day, setDay, month, setMonth, year, setYear, era, setEra, required = true }) => (
  <fieldset style={{ marginBottom: 20, padding: '14px 16px', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
    <legend style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', padding: '0 4px' }}>{label}</legend>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
      <div>
        <label htmlFor={`${prefix}-day`} style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
          Day <span style={{ color: 'var(--text3)' }}>(optional)</span>
        </label>
        <input
          id={`${prefix}-day`}
          type="text"
          inputMode="numeric"
          value={day}
          onChange={e => setDay(e.target.value.replace(/\D/g, ''))}
          placeholder="e.g. 15"
        />
      </div>
      <div>
        <label htmlFor={`${prefix}-month`} style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
          Month <span style={{ color: 'var(--text3)' }}>(optional)</span>
        </label>
        <select id={`${prefix}-month`} value={month} onChange={e => setMonth(e.target.value)}>
          <option value="">— Select month —</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor={`${prefix}-year`} style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
          Year {required ? <span style={{ color: 'var(--danger)' }} aria-label="required">*</span> : <span style={{ color: 'var(--text3)' }}>(optional)</span>}
        </label>
        <input
          id={`${prefix}-year`}
          type="text"
          inputMode="numeric"
          value={year}
          onChange={e => setYear(e.target.value.replace(/\D/g, ''))}
          placeholder="e.g. 1066"
          aria-required={required}
        />
      </div>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Era</legend>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            ['B.C.',       'BC',   null],
            ["Don't Show", 'NONE', 'No era suffix will be shown on the timeline.'],
          ].map(([lbl, val, tip]) => (
            <button
              key={val}
              type="button"
              aria-pressed={era === val}
              onClick={() => setEra(val)}
              title={tip || undefined}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 'var(--radius)',
                border: `1px solid ${era === val ? 'var(--accent)' : 'var(--border2)'}`,
                background: era === val ? 'var(--accent)22' : 'transparent',
                color: era === val ? 'var(--accent)' : 'var(--text2)',
                cursor: 'pointer', fontSize: 13, fontWeight: era === val ? 600 : 400,
              }}
            >{lbl}</button>
          ))}
        </div>
      </fieldset>
    </div>
  </fieldset>
);

// ─── LOCATION PICKER ─────────────────────────────────────────────────────────

const LocationPicker = ({ location, onChange }) => {
  // Parse existing name into city/country if it contains a comma
  const parseName = (name) => {
    if (!name) return { city: '', country: '' };
    const parts = name.split(',').map(s => s.trim());
    return { city: parts[0] || '', country: parts[1] || '' };
  };
  const parsed = parseName(location?.name);

  const [city,        setCity]        = useState(parsed.city);
  const [country,     setCountry]     = useState(parsed.country);
  const [loading,     setLoading]     = useState(false);
  const [geoError,    setGeoError]    = useState('');
  const [showManual,  setShowManual]  = useState(false);
  const [zoom,        setZoom]        = useState(location?.zoom || 5);

  const mapElRef   = useRef(null);
  const mapRef     = useRef(null);
  const markerRef  = useRef(null);
  const debRef     = useRef(null);

  const hasCoords = (loc) =>
    typeof loc?.lat === 'number' && isFinite(loc.lat) &&
    typeof loc?.lng === 'number' && isFinite(loc.lng);

  // Init mini-map
  useEffect(() => {
    const el = mapElRef.current;
    if (!el || mapRef.current) return;

    const map = L.map(el, {
      center: hasCoords(location) ? [location.lat, location.lng] : [20, 0],
      zoom:   hasCoords(location) ? (location.zoom || 5) : 2,
      zoomControl: true,
      attributionControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    if (hasCoords(location)) {
      markerRef.current = L.marker([location.lat, location.lng]).addTo(map);
    }

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current  = null;
      markerRef.current = null;
    };
  }, []); // only once

  // Update mini-map when location changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    if (hasCoords(location)) {
      markerRef.current = L.marker([location.lat, location.lng]).addTo(map);
      map.flyTo([location.lat, location.lng], location.zoom || zoom, { duration: 0.4 });
    }
  }, [location]);

  const geocodeCityCountry = useCallback((c, co) => {
    if (debRef.current) clearTimeout(debRef.current);
    const q = [c, co].filter(Boolean).join(', ').trim();
    if (!q) return;
    debRef.current = setTimeout(async () => {
      setLoading(true);
      setGeoError('');
      try {
        const data = await nominatimSearch(q);
        if (data.length === 0) {
          setGeoError('Location not found — check spelling or use Manual coordinates.');
          return;
        }
        const hit  = data[0];
        const name = [c.trim(), co.trim()].filter(Boolean).join(', ');
        onChange({ name, lat: parseFloat(hit.lat), lng: parseFloat(hit.lon), zoom });
      } catch (err) {
        if (err.status === 429) {
          setGeoError(err.message);
        } else {
          setGeoError('Geocoding failed — check your connection or use Manual coordinates.');
        }
      } finally {
        setLoading(false);
      }
    }, 400);
  }, [zoom, onChange]);

  const handleManualChange = (field, raw) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    onChange({
      name: location?.name || 'Custom location',
      lat:  field === 'lat' ? val : (location?.lat || 0),
      lng:  field === 'lng' ? val : (location?.lng || 0),
      zoom,
    });
  };

  return (
    <div className="mp-loc-picker">
      {/* City / Country / Manual toggle */}
      <div className="mp-loc-mode">
        <button
          type="button"
          className={`mp-loc-mode-btn${!showManual ? ' mp-loc-mode-active' : ''}`}
          onClick={() => setShowManual(false)}
        >
          City &amp; Country
        </button>
        <button
          type="button"
          className={`mp-loc-mode-btn${showManual ? ' mp-loc-mode-active' : ''}`}
          onClick={() => setShowManual(true)}
        >
          Coordinates
        </button>
      </div>

      {!showManual ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>City</label>
              <input
                value={city}
                onChange={e => {
                  setCity(e.target.value);
                  setGeoError('');
                  geocodeCityCountry(e.target.value, country);
                }}
                placeholder="e.g. Paris"
                autoComplete="off"
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Country</label>
              <input
                value={country}
                onChange={e => {
                  setCountry(e.target.value);
                  setGeoError('');
                  geocodeCityCountry(city, e.target.value);
                }}
                placeholder="e.g. France"
                autoComplete="off"
              />
            </div>
          </div>
          {loading && (
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>Looking up coordinates…</div>
          )}
          {!loading && location?.lat && !geoError && (
            <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Icon name="check" size={12} />
              {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
            </div>
          )}
          {geoError && (
            <div style={{ fontSize: 12, color: 'var(--danger)' }}>{geoError}</div>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Latitude</label>
            <input
              type="number"
              step="any"
              value={location?.lat ?? ''}
              onChange={e => handleManualChange('lat', e.target.value)}
              placeholder="e.g. 48.8566"
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Longitude</label>
            <input
              type="number"
              step="any"
              value={location?.lng ?? ''}
              onChange={e => handleManualChange('lng', e.target.value)}
              placeholder="e.g. 2.3522"
            />
          </div>
        </div>
      )}

      {/* Mini map */}
      <div ref={mapElRef} className="mp-loc-minimap" />

      {/* Zoom slider */}
      {location?.lat != null && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>
              Map zoom when active
            </label>
            <span style={{ fontSize: 12, color: 'var(--text3)', fontVariantNumeric: 'tabular-nums' }}>
              {zoom}
            </span>
          </div>
          <input
            type="range"
            min="1"
            max="18"
            value={zoom}
            onChange={e => {
              const z = parseInt(e.target.value, 10);
              setZoom(z);
              if (location) onChange({ ...location, zoom: z });
            }}
            style={{ width: '100%' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
            <span>World</span>
            <span>Street</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

// Dropdown labels for the parent/spouse/adopted-by selects show the title +
// a date suffix when available so two events that share a title (common in
// Roman dynasties — multiple "Gaius Julius Caesar" generations) can be
// told apart and the user can't accidentally pick the wrong one (which
// previously could create a self-loop that hid the node from the tree).
const eventOptionLabel = (ev) => {
  const title = (ev.title || '(untitled)').slice(0, 50);
  const dateStr = ev.startDate ? formatDate(ev.startDate, ev.startEra !== 'NONE') : '';
  return dateStr ? `${title} — ${dateStr}` : title;
};

const EventForm = ({ initial, onSave, onCancel, existingChapters = [], requireLocation = false, existingSwimlanes = [], showProgress = false, showTreeFields = false, showSpineFields = false, showFeatured = false, showNewspaperFields = false, showMapFields = false, existingEvents = [], editingEventId = null }) => {

  const parseDate = (key) => {
    if (!key) return { day: '', month: '', year: '', era: 'NONE' };
    if (key.length === 12) {
      const { day, month, year, era } = fromDateKey(key);
      // 0 means "not entered" — convert back to empty string for the form fields
      return { day: day === 0 ? '' : day, month: month === 0 ? '' : month, year, era: era === 'AD' ? 'NONE' : era };
    }
    const [y, m, d] = key.split('-').map(Number);
    return { day: d, month: m, year: y, era: 'NONE' };
  };

  const initS = parseDate(initial?.startDate);
  const initE = parseDate(initial?.endDate && initial.endDate !== initial.startDate ? initial.endDate : null);

  // Split on mount so the RTE only holds text and the thumbnail strip holds images.
  const initDescParts = extractDescParts(initial?.description);

  const [title,      setTitle]      = useState(initial?.title       || '');
  const [startDay,   setStartDay]   = useState(initS.day);
  const [startMonth, setStartMonth] = useState(initS.month);
  const [startYear,  setStartYear]  = useState(initS.year);
  const normalizeEra = (e) => e === 'AD' ? 'NONE' : (e || 'NONE');
  const [startEra,   setStartEra]   = useState(normalizeEra(initial?.startEra ?? (initial ? initS.era : 'NONE')));
  const [endDay,     setEndDay]     = useState(initE.day);
  const [endMonth,   setEndMonth]   = useState(initE.month);
  const [endYear,    setEndYear]    = useState(initE.year);
  const [endEra,     setEndEra]     = useState(normalizeEra(initial?.endEra   ?? (initial ? initE.era : 'NONE')));
  const [desc,       setDesc]       = useState(initDescParts.text);
  const [sources,    setSources]    = useState(initial?.sources || []);
  const [chapter,         setChapter]         = useState(initial?.chapter || '');
  const [swimlane,        setSwimlane]        = useState(initial?.swimlane || '');
  const [progress,        setProgress]        = useState(initial?.progress != null ? String(initial.progress) : '');
  const [pullQuote,       setPullQuote]       = useState(initial?.pullQuote || '');
  const [featured,        setFeatured]        = useState(initial?.featured || false);
  const [breakingNews,    setBreakingNews]    = useState(initial?.breakingNews || false);
  const [parentEventId,       setParentEventId]       = useState(initial?.parentEventId || '');
  const [secondParentEventId, setSecondParentEventId] = useState(initial?.secondParentEventId || '');
  const [branchLabel,         setBranchLabel]         = useState(initial?.branchLabel || '');
  const [branchColor,         setBranchColor]         = useState(initial?.branchColor || '');
  const [spouseEventIds,      setSpouseEventIds]      = useState(
    Array.isArray(initial?.spouseEventIds) ? initial.spouseEventIds.filter(Boolean) : []
  );
  const [adoptedByEventIds,   setAdoptedByEventIds]   = useState(
    Array.isArray(initial?.adoptedByEventIds) ? initial.adoptedByEventIds.filter(Boolean) : []
  );
  const [photo,           setPhoto]           = useState(initial?.photo || null);
  const [location,        setLocation]        = useState(initial?.location || null);
  const [showLocPicker,   setShowLocPicker]   = useState(requireLocation || !!initial?.location);
  // Map-layout per-event icon + badge size (only used when showMapFields)
  const [mapIcon,         setMapIcon]         = useState(initial?.mapIcon || 'pin');
  const [mapMarkerSize,   setMapMarkerSize]   = useState(initial?.mapMarkerSize || 'medium');
  const [descImages,      setDescImages]      = useState(initDescParts.srcs);
  const [descImgUploading,setDescImgUploading] = useState(false);
  const [error,           setError]           = useState('');
  const [photoError,      setPhotoError]      = useState('');


  const [tags,            setTags]            = useState(initial?.tags || []);
  const [tagInput,        setTagInput]        = useState('');
  const [tagSuggestions,  setTagSuggestions]  = useState([]);
  const tagDebounce = useRef(null);

  const [isDirty,         setIsDirty]         = useState(false);
  const [showLeaveWarning,setShowLeaveWarning] = useState(false);

  const [videoUrlInput,   setVideoUrlInput]   = useState(initial?.videoUrl || '');
  const [videoUrlError,   setVideoUrlError]   = useState('');

  const validateYouTubeUrl = (url) => {
    if (!url) return true;
    try {
      const parsed = new URL(url.trim());
      const host   = parsed.hostname.replace('www.', '').replace('m.', '');
      return host === 'youtube.com' || host === 'youtu.be' || host === 'youtube-nocookie.com';
    } catch {
      return false;
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url.trim());
      const host   = parsed.hostname.replace('www.', '').replace('m.', '');
      let id = null;
      if (host === 'youtu.be') {
        id = parsed.pathname.slice(1).split('/')[0];
      } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
        if (parsed.pathname.startsWith('/shorts/') || parsed.pathname.startsWith('/embed/')) {
          id = parsed.pathname.split('/')[2];
        } else {
          id = parsed.searchParams.get('v');
        }
      }
      if (!id || !/^[a-zA-Z0-9_-]{11}$/.test(id)) return null;
      return `https://www.youtube-nocookie.com/embed/${id}`;
    } catch {
      return null;
    }
  };

  const withDirty = (setter) => (v) => { setter(v); setIsDirty(true); };

  // ── Tag helpers ───────────────────────────────────────────────────────────

  const normalizeTag = (t) => t.trim().toLowerCase().replace(/\s+/g, '-').slice(0, 50);

  const addTag = (raw) => {
    const tag = normalizeTag(raw);
    if (!tag || tags.includes(tag)) { setTagInput(''); setTagSuggestions([]); return; }
    setTags(prev => [...prev, tag]);
    setTagInput('');
    setTagSuggestions([]);
    setIsDirty(true);
  };

  const removeTag = (tag) => { setTags(prev => prev.filter(t => t !== tag)); setIsDirty(true); };

  const handleTagInputChange = (val) => {
    setTagInput(val);
    clearTimeout(tagDebounce.current);
    if (!val.trim()) { setTagSuggestions([]); return; }
    tagDebounce.current = setTimeout(async () => {
      try {
        const { tags: sugs } = await Storage.getTags(val.trim());
        setTagSuggestions((sugs || []).filter(s => !tags.includes(s)));
      } catch { setTagSuggestions([]); }
    }, 180);
  };

  const handleTagKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleCancel = () => {
    if (isDirty) { setShowLeaveWarning(true); return; }
    onCancel();
  };

  // ── Sources ───────────────────────────────────────────────────────────────

  const addSource    = ()          => { setSources(prev => [...prev, { id: uid(), label: '', text: '' }]); setIsDirty(true); };
  const removeSource = (id)        => { setSources(prev => prev.filter(s => s.id !== id)); setIsDirty(true); };
  const updateSource = (id, patch) => { setSources(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s)); setIsDirty(true); };

  // ── Event photo ───────────────────────────────────────────────────────────

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoError('');
    try {
      const url = await Storage.uploadImage(file, 'event-photo');
      setPhoto(url);
      setIsDirty(true);
    } catch (err) {
      setPhotoError('Photo upload failed. Please try again.');
    }
  };

  // ── Description images ────────────────────────────────────────────────────

  // One photo max per event description.  Existing events with multiple
  // photos (uploaded before this limit) are not modified — but any new
  // attempt to upload a second photo is blocked with an inline warning.
  const [descImgLimitMsg, setDescImgLimitMsg] = useState('');
  const handleDescImg = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (descImages.length >= 1) {
      setDescImgLimitMsg('Only one photo is allowed per event description. Remove the existing photo before uploading a new one.');
      return;
    }
    setDescImgLimitMsg('');
    setDescImgUploading(true);
    try {
      const url = await Storage.uploadImage(file, 'description-image');
      setDescImages(prev => [...prev, url]);
      setIsDirty(true);
    } catch (err) {
      setError('Description image upload failed. Please try again.');
    } finally {
      setDescImgUploading(false);
    }
  };

  const removeDescImg = (url) => {
    setDescImages(prev => prev.filter(u => u !== url));
    setDescImgLimitMsg('');
    setIsDirty(true);
  };

  // ── Date helpers ──────────────────────────────────────────────────────────

  const buildKey = (day, month, year, era) => {
    const y = parseInt(year, 10);
    if (!y || y < 1) return null;
    const d = parseInt(day, 10) || 0;   // 0 = "not entered"
    const m = parseInt(month, 10) || 0; // 0 = "not entered"
    return toDateKey(y, m, d, era === 'BC' ? 'BC' : 'AD');
  };

  // ── Save ─────────────────────────────────────────────────────────────────

  const save = () => {
    if (!title.trim()) { setError('Title is required.'); return; }

    const startKey = buildKey(startDay, startMonth, startYear, startEra);
    const endKey   = buildKey(endDay,   endMonth,   endYear,   endEra);
    if (!startKey) { setError('Start year is required.'); return; }
    if (endKey && endKey < startKey) { setError('End date must be on or after start date.'); return; }

    if (requireLocation && !location?.lat) {
      setError('City and country are required for Map layout events.');
      setShowLocPicker(true);
      return;
    }

    const badSource = sources.find(s => efCountWords(s.text) > EF_SOURCE_LIMIT);
    if (badSource) {
      setError(`"${badSource.label || 'Untitled source'}" exceeds the ${EF_SOURCE_LIMIT.toLocaleString()}-word limit.`);
      return;
    }

    const descImgHtml = descImages.map(url =>
      `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:6px;margin:6px 0;display:block;" />`
    ).join('');

    onSave({
      id:          initial?.id || uid(),
      title:       title.trim(),
      startDate:   startKey,
      endDate:     endKey || startKey,
      startEra,
      endEra,
      description: desc.trim() + descImgHtml,
      sources:     sources.filter(s => s.label.trim() || s.text.trim()),
      photo:          photo || null,
      chapter:        chapter.trim() || null,
      location:       location || null,
      mapIcon:        showMapFields ? (mapIcon || 'pin') : (initial?.mapIcon || null),
      mapMarkerSize:  showMapFields ? (mapMarkerSize || 'medium') : (initial?.mapMarkerSize || null),
      swimlane:       swimlane.trim() || null,
      progress:       progress !== '' ? Math.min(100, Math.max(0, parseInt(progress, 10))) : null,
      dependsOn:      initial?.dependsOn || null,
      parentEventId:       parentEventId || null,
      secondParentEventId: secondParentEventId || null,
      spouseEventIds:      spouseEventIds.length > 0 ? spouseEventIds : null,
      adoptedByEventIds:   adoptedByEventIds.length > 0 ? adoptedByEventIds : null,
      branchLabel:         branchLabel.trim() || null,
      branchColor:         branchColor || null,
      pullQuote:      pullQuote.trim() || null,
      featured:       featured || null,
      breakingNews:   breakingNews || null,
      tags:           tags.length > 0 ? tags : null,
      videoUrl:       videoUrlInput.trim() || null,
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>

      {/* Title */}
      <div style={{ marginBottom: 16 }}>
        <label htmlFor="ef-title">Event Title</label>
        <input
          id="ef-title"
          value={title}
          onChange={e => withDirty(setTitle)(e.target.value)}
          placeholder="e.g. Project Kickoff"
          aria-required="true"
        />
      </div>

      {/* Start date */}
      <DateFields
        label="Start Date"
        prefix="start"
        day={startDay}     setDay={withDirty(setStartDay)}
        month={startMonth} setMonth={withDirty(setStartMonth)}
        year={startYear}   setYear={withDirty(setStartYear)}
        era={startEra}     setEra={withDirty(setStartEra)}
      />

      {/* End date */}
      <DateFields
        label="End Date (optional)"
        prefix="end"
        day={endDay}     setDay={withDirty(setEndDay)}
        month={endMonth} setMonth={withDirty(setEndMonth)}
        year={endYear}   setYear={withDirty(setEndYear)}
        era={endEra}     setEra={withDirty(setEndEra)}
        required={false}
      />

      {/* Event Photo */}
      <div style={{ marginBottom: 20 }}>
        <label>Event Photo <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
        {photo ? (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
              src={photo}
              alt="Event photo preview"
              style={{ display: 'block', maxWidth: '100%', maxHeight: 200, borderRadius: 'var(--radius)', border: '1px solid var(--border)', objectFit: 'cover' }}
            />
            <button
              type="button"
              onClick={() => { setPhoto(null); setIsDirty(true); }}
              aria-label="Remove event photo"
              style={{
                position: 'absolute', top: 6, right: 6,
                background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: 6,
                color: '#fff', fontSize: 12, padding: '3px 8px', cursor: 'pointer',
              }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div
              style={{
                width: '100%', padding: '14px 0',
                background: 'transparent',
                border: '2px dashed var(--border2)',
                borderRadius: 'var(--radius)',
                color: 'var(--text3)', fontSize: 13,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Icon name="plus" size={14} /> Upload Photo
            </div>
            <input
              type="file"
              accept="image/*"
              aria-label="Upload event photo"
              onChange={handlePhoto}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                opacity: 0, cursor: 'pointer',
              }}
            />
          </div>
        )}
        {photoError && (
          <p role="alert" style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6, marginBottom: 0 }}>
            {photoError}
          </p>
        )}
      </div>

      {/* Description */}
      <div style={{ marginBottom: 20 }}>
        <label>
          Description <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span>
        </label>
        <RichTextEditor
          value={desc}
          onChange={v => withDirty(setDesc)(v)}
          maxLength={EF_DESC_MAX_CHARS}
          placeholder="What happened during this event?"
        />

        {/* ── Description images ── */}
        <div style={{ marginTop: 8 }}>
          <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Description Images
          </p>

          {descImages.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              {descImages.map(url => (
                <div key={url} style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={url}
                    alt="Description image"
                    style={{
                      height: 80, width: 'auto', maxWidth: 150,
                      objectFit: 'cover',
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--border)',
                      display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeDescImg(url)}
                    aria-label="Remove image"
                    style={{
                      position: 'absolute', top: 4, right: 4,
                      background: 'rgba(0,0,0,0.65)', border: 'none',
                      borderRadius: 4, color: '#fff',
                      fontSize: 11, padding: '2px 7px', cursor: 'pointer',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ position: 'relative', opacity: descImgUploading ? 0.6 : 1 }}>
            <div
              style={{
                width: '100%', padding: '12px 0',
                background: 'transparent',
                border: '2px dashed var(--border2)',
                borderRadius: 'var(--radius)',
                color: 'var(--text3)', fontSize: 13,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <Icon name="image" size={15} />
              {descImgUploading ? 'Uploading…' : 'Add Image to Description'}
            </div>
            <input
              type="file"
              accept="image/*"
              aria-label="Add image to description"
              onChange={handleDescImg}
              disabled={descImgUploading}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                opacity: 0, cursor: descImgUploading ? 'not-allowed' : 'pointer',
                pointerEvents: descImgUploading ? 'none' : 'auto',
              }}
            />
          </div>
          {descImgLimitMsg && (
            <p role="alert" style={{
              marginTop: 8,
              fontSize: 12,
              color: 'var(--danger)',
              lineHeight: 1.4,
            }}>
              {descImgLimitMsg}
            </p>
          )}
        </div>
      </div>

      {/* YouTube Video */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 }}>
          YouTube Video <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
        </label>
        <input
          type="url"
          placeholder="https://www.youtube.com/watch?v=..."
          value={videoUrlInput}
          onChange={e => { setVideoUrlInput(e.target.value); setVideoUrlError(''); setIsDirty(true); }}
          onBlur={() => {
            if (videoUrlInput && !validateYouTubeUrl(videoUrlInput)) {
              setVideoUrlError('Please enter a valid YouTube URL.');
            }
          }}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 6,
            border: `1px solid ${videoUrlError ? 'var(--error, #e05252)' : 'var(--border)'}`,
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: 13,
            boxSizing: 'border-box',
          }}
        />
        {videoUrlError && (
          <p style={{ fontSize: 11, color: 'var(--error, #e05252)', marginTop: 4 }}>{videoUrlError}</p>
        )}
        {videoUrlInput && !videoUrlError && validateYouTubeUrl(videoUrlInput) && (() => {
          const embedUrl = getYouTubeEmbedUrl(videoUrlInput);
          return embedUrl ? (
            <div style={{ marginTop: 10, borderRadius: 8, overflow: 'hidden', background: '#000', aspectRatio: '16/9' }}>
              <iframe
                src={embedUrl}
                title="YouTube preview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            </div>
          ) : (
            <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Video will appear on this event</p>
          );
        })()}
      </div>

      {/* Progress */}
      {showProgress && (
        <div style={{ marginBottom: 20 }}>
          <label htmlFor="ef-progress">
            Progress <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(0 – 100%)</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              id="ef-progress"
              type="number"
              min="0"
              max="100"
              value={progress}
              onChange={e => withDirty(setProgress)(e.target.value)}
              placeholder="e.g. 75"
              style={{ width: 90 }}
            />
            <input
              type="range"
              min="0"
              max="100"
              value={progress === '' ? 0 : progress}
              onChange={e => withDirty(setProgress)(e.target.value)}
              style={{ flex: 1 }}
              aria-label="Progress percentage slider"
            />
            <span style={{ fontSize: 13, color: 'var(--text2)', minWidth: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {progress === '' ? '—' : `${progress}%`}
            </span>
          </div>
        </div>
      )}

      {/* Featured (zigzag only) */}
      {showFeatured && (
        <div style={{ marginBottom: 20, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!featured}
              onChange={e => withDirty(setFeatured)(e.target.checked)}
              style={{ width: 16, height: 16, flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: 'var(--text)' }}>
              Featured event
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                Shown full-width across the zigzag spine with a ★ badge
              </span>
            </span>
          </label>
        </div>
      )}

      {/* Spine fields */}
      {showSpineFields && (
        <div style={{ marginBottom: 20, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
            Spine Chapter <span style={{ fontWeight: 400 }}>(optional)</span>
          </p>
          <div>
            <label htmlFor="ef-pull-quote" style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
              Pull Quote
              <span style={{ fontWeight: 400, marginLeft: 6 }}>— overrides the auto-extracted quote</span>
            </label>
            <textarea
              id="ef-pull-quote"
              value={pullQuote}
              onChange={e => withDirty(setPullQuote)(e.target.value)}
              placeholder="A memorable sentence to feature as a pull quote…"
              rows={3}
              style={{ width: '100%', resize: 'vertical', fontStyle: pullQuote ? 'normal' : 'italic' }}
            />
          </div>
        </div>
      )}

      {/* Tree fields */}
      {showTreeFields && (
        <div style={{ marginBottom: 20, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
            Tree Structure <span style={{ fontWeight: 400 }}>(optional)</span>
          </p>

          {/* Biological Parent 1 (formerly Parent Event) */}
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="ef-parent" style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
              Biological Parent 1
            </label>
            <select
              id="ef-parent"
              value={parentEventId}
              onChange={e => withDirty(setParentEventId)(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">— None (root / trunk event) —</option>
              {existingEvents
                .filter(ev => ev.id !== editingEventId && ev.id !== secondParentEventId)
                .sort((a, b) => a.title.localeCompare(b.title))
                .map(ev => (
                  <option key={ev.id} value={ev.id}>{eventOptionLabel(ev)}</option>
                ))}
            </select>
          </div>

          {/* Biological Parent 2 — triggers wishbone render when both set. */}
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="ef-parent-2" style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
              Biological Parent 2
            </label>
            <select
              id="ef-parent-2"
              value={secondParentEventId}
              onChange={e => withDirty(setSecondParentEventId)(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">— None —</option>
              {existingEvents
                .filter(ev => ev.id !== editingEventId && ev.id !== parentEventId)
                .sort((a, b) => a.title.localeCompare(b.title))
                .map(ev => (
                  <option key={ev.id} value={ev.id}>{eventOptionLabel(ev)}</option>
                ))}
            </select>
            <p style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, lineHeight: 1.4 }}>
              When both parents are set a wishbone connector links the child to both.
              Connect the parents with a spousal line for the most accurate family tree rendering.
            </p>
          </div>

          {/* Spouse / partner — multi-select.  Renders a dropdown to add, and
              chip-style remove buttons for each currently linked partner. */}
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="ef-spouse" style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
              Spouse or Partner
            </label>
            {spouseEventIds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {spouseEventIds.map(sid => {
                  const sev = existingEvents.find(e => e.id === sid);
                  if (!sev) return null;
                  return (
                    <span key={sid} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 6px 3px 8px',
                      fontSize: 11,
                      background: 'rgba(201, 169, 110, 0.15)',
                      border: '1px solid rgba(201, 169, 110, 0.4)',
                      borderRadius: 12,
                      color: 'var(--text2)',
                    }}>
                      <span>♥ {sev.title.slice(0, 30)}</span>
                      <button
                        type="button"
                        onClick={() => withDirty(setSpouseEventIds)(spouseEventIds.filter(x => x !== sid))}
                        aria-label={`Remove ${sev.title} as spouse`}
                        title="Remove"
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--text3)', fontSize: 14, lineHeight: 1, padding: '0 2px',
                        }}
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}
            <select
              id="ef-spouse"
              value=""
              onChange={e => {
                const sid = e.target.value;
                if (!sid) return;
                if (spouseEventIds.includes(sid)) return;
                withDirty(setSpouseEventIds)([...spouseEventIds, sid]);
              }}
              style={{ width: '100%' }}
            >
              <option value="">— Add a spouse or partner —</option>
              {existingEvents
                .filter(ev => ev.id !== editingEventId && !spouseEventIds.includes(ev.id))
                .sort((a, b) => a.title.localeCompare(b.title))
                .map(ev => (
                  <option key={ev.id} value={ev.id}>{eventOptionLabel(ev)}</option>
                ))}
            </select>
            <p style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, lineHeight: 1.4 }}>
              Spousal connections show as dashed lines on the tree. They do not affect the branch hierarchy.
            </p>
          </div>

          {/* Adopted By — multi-select.  Same chip + remove × pattern as
              the Spouse field. */}
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="ef-adopted-by" style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
              Adopted By
            </label>
            {adoptedByEventIds.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {adoptedByEventIds.map(aid => {
                  const aev = existingEvents.find(e => e.id === aid);
                  if (!aev) return null;
                  return (
                    <span key={aid} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 6px 3px 8px',
                      fontSize: 11,
                      background: 'rgba(201, 169, 110, 0.12)',
                      border: '1px dotted rgba(201, 169, 110, 0.55)',
                      borderRadius: 12,
                      color: 'var(--text2)',
                    }}>
                      <span>📜 {aev.title.slice(0, 30)}</span>
                      <button
                        type="button"
                        onClick={() => withDirty(setAdoptedByEventIds)(adoptedByEventIds.filter(x => x !== aid))}
                        aria-label={`Remove ${aev.title} as adopter`}
                        title="Remove"
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--text3)', fontSize: 14, lineHeight: 1, padding: '0 2px',
                        }}
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}
            <select
              id="ef-adopted-by"
              value=""
              onChange={e => {
                const aid = e.target.value;
                if (!aid) return;
                if (adoptedByEventIds.includes(aid)) return;
                withDirty(setAdoptedByEventIds)([...adoptedByEventIds, aid]);
              }}
              style={{ width: '100%' }}
            >
              <option value="">— Add an adoptive parent —</option>
              {existingEvents
                .filter(ev => ev.id !== editingEventId && !adoptedByEventIds.includes(ev.id))
                .sort((a, b) => a.title.localeCompare(b.title))
                .map(ev => (
                  <option key={ev.id} value={ev.id}>{eventOptionLabel(ev)}</option>
                ))}
            </select>
            <p style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, lineHeight: 1.4 }}>
              Adoption connections show as dotted lines on the tree. They do not affect the branch hierarchy.
            </p>
          </div>

          {/* Branch label */}
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="ef-branch-label" style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
              Branch Label
            </label>
            <input
              id="ef-branch-label"
              value={branchLabel}
              onChange={e => withDirty(setBranchLabel)(e.target.value)}
              placeholder="e.g. Northern Campaign, Phase 2…"
              autoComplete="off"
            />
          </div>

          {/* Branch color */}
          <div>
            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, display: 'block' }}>
              Branch Color
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="color"
                value={branchColor || '#4A8FD4'}
                onChange={e => withDirty(setBranchColor)(e.target.value)}
                style={{ width: 36, height: 36, border: '1px solid var(--border2)', borderRadius: 4, cursor: 'pointer', padding: 2 }}
              />
              {branchColor ? (
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => withDirty(setBranchColor)('')}
                  style={{ fontSize: 11, color: 'var(--text3)' }}
                >
                  Clear (inherit parent color)
                </button>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>Inherits parent branch color</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Layout Specific Fields */}
      <fieldset style={{ marginBottom: 20, padding: '14px 16px', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
        <legend style={{ fontWeight: 600, fontSize: 11, color: 'var(--text3)', padding: '0 6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Layout Specific Fields
        </legend>

        {/* Chapter */}
        <div style={{ marginBottom: 16, marginTop: 8 }}>
          <label htmlFor="ef-chapter">
            Chapter <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional — Storyline layout)</span>
          </label>
          <input
            id="ef-chapter"
            list="ef-chapter-list"
            value={chapter}
            onChange={e => { withDirty(setChapter)(e.target.value); }}
            placeholder="e.g. Phase 1, Investigation, Recovery…"
            autoComplete="off"
          />
          {existingChapters.length > 0 && (
            <datalist id="ef-chapter-list">
              {existingChapters.map(c => <option key={c} value={c} />)}
            </datalist>
          )}
        </div>

        {/* Swimlane */}
        <div style={{ marginBottom: showNewspaperFields ? 16 : 0 }}>
          <label htmlFor="ef-swimlane">
            Swimlane <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional — Swimlane layout)</span>
          </label>
          <input
            id="ef-swimlane"
            list="ef-swimlane-list"
            value={swimlane}
            onChange={e => withDirty(setSwimlane)(e.target.value)}
            placeholder="e.g. Design, Engineering, QA…"
            autoComplete="off"
          />
          {existingSwimlanes.length > 0 && (
            <datalist id="ef-swimlane-list">
              {existingSwimlanes.map(s => <option key={s} value={s} />)}
            </datalist>
          )}
        </div>

        {/* Newspaper layout */}
        {showNewspaperFields && (
          <div style={{ marginBottom: 16, background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
              Newspaper Layout
            </p>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={!!featured}
                onChange={e => withDirty(setFeatured)(e.target.checked)}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: 'var(--text)' }}>
                ⭐ Feature this event
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  Spans two columns in the newspaper layout
                </span>
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
              <input
                type="checkbox"
                checked={!!breakingNews}
                onChange={e => withDirty(setBreakingNews)(e.target.checked)}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: 'var(--text)' }}>
                🔴 Breaking News
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  Pins this event to the breaking news banner and ticker
                </span>
              </span>
            </label>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <label style={{ fontSize: 11, color: 'var(--text3)', margin: 0 }}>
                  Pull Quote Override
                  <span style={{ fontWeight: 400, marginLeft: 6 }}>— overrides the auto-extracted quote</span>
                </label>
                <span style={{ fontSize: 11, color: pullQuote.length > 190 ? 'var(--danger)' : 'var(--text3)' }}>
                  {pullQuote.length} / 200
                </span>
              </div>
              <textarea
                value={pullQuote}
                onChange={e => withDirty(setPullQuote)(e.target.value.slice(0, 200))}
                placeholder="A memorable sentence to feature as a pull quote…"
                rows={3}
                style={{ width: '100%', resize: 'vertical', fontStyle: pullQuote ? 'normal' : 'italic' }}
              />
            </div>
          </div>
        )}

        {/* Location */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ margin: 0 }}>
              Location{' '}
              {requireLocation
                ? <span style={{ color: 'var(--danger)' }}>*</span>
                : <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional — Map layout)</span>
              }
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {location && (
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => { setLocation(null); setShowLocPicker(requireLocation); setIsDirty(true); }}
                  style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Icon name="trash" size={12} /> Clear
                </button>
              )}
              {!showLocPicker && !requireLocation && (
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setShowLocPicker(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Icon name="map" size={12} /> Add Location
                </button>
              )}
            </div>
          </div>

          {location && !showLocPicker && (
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--surface)', border: '1px solid var(--border2)',
                borderRadius: 'var(--radius)', padding: '8px 12px',
                fontSize: 13, color: 'var(--text2)',
              }}
            >
              <Icon name="map" size={14} />
              <span style={{ flex: 1 }}>{location.name}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontVariantNumeric: 'tabular-nums' }}>
                {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
              </span>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => setShowLocPicker(true)}
                style={{ padding: '2px 8px', fontSize: 12 }}
              >
                Edit
              </button>
            </div>
          )}

          {showLocPicker && (
            <div
              style={{
                background: 'var(--surface)', border: '1px solid var(--border2)',
                borderRadius: 'var(--radius)', padding: '14px 16px',
              }}
            >
              <LocationPicker
                location={location}
                onChange={loc => { setLocation(loc); setIsDirty(true); }}
              />
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setShowLocPicker(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Marker Icon — map layout only */}
        {showMapFields && (
          <div style={{ marginTop: 18 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Marker Icon <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
              {[
                { id: 'pin',    emoji: '📍', label: 'Default Pin' },
                { id: 'sword',  emoji: '⚔️', label: 'Sword' },
                { id: 'crown',  emoji: '👑', label: 'Crown' },
                { id: 'column', emoji: '🏛️', label: 'Column' },
                { id: 'skull',  emoji: '💀', label: 'Skull' },
                { id: 'flame',  emoji: '🔥', label: 'Flame' },
                { id: 'ship',   emoji: '🚢', label: 'Ship' },
                { id: 'book',   emoji: '📖', label: 'Book' },
                { id: 'coin',   emoji: '🪙', label: 'Coin' },
                { id: 'star',   emoji: '⭐', label: 'Star' },
                { id: 'flag',   emoji: '🚩', label: 'Flag' },
                { id: 'person', emoji: '👤', label: 'Person' },
              ].map(({ id, emoji, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setMapIcon(id); setIsDirty(true); }}
                  title={label}
                  aria-label={label}
                  aria-pressed={mapIcon === id}
                  style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    fontSize: 18,
                    background: mapIcon === id ? 'var(--accent)' : 'var(--bg2)',
                    border: `1px solid ${mapIcon === id ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'transform 0.1s',
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>

            {/* Marker size */}
            <div style={{ marginTop: 14 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>
                Marker Size
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { id: 'small',  label: 'Small'  },
                  { id: 'medium', label: 'Medium' },
                  { id: 'large',  label: 'Large'  },
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => { setMapMarkerSize(id); setIsDirty(true); }}
                    aria-pressed={mapMarkerSize === id}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      fontSize: 12,
                      fontWeight: mapMarkerSize === id ? 700 : 500,
                      background: mapMarkerSize === id ? 'var(--accent)' : 'var(--bg2)',
                      color: mapMarkerSize === id ? '#1A1408' : 'var(--text)',
                      border: `1px solid ${mapMarkerSize === id ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius)',
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </fieldset>

      {/* Sources */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <label style={{ margin: 0 }}>Sources <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
          <button
            type="button"
            onClick={addSource}
            className="btn-ghost btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Icon name="plus" size={13} /> Add Source
          </button>
        </div>

        {sources.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>No sources added yet.</p>
        )}

        {sources.map((src, i) => {
          const srcWords   = efCountWords(src.text);
          const srcCountId = `src-count-${src.id}`;
          const srcLabelId = `src-label-${src.id}`;
          const srcTextId  = `src-text-${src.id}`;

          return (
            <div
              key={src.id}
              style={{
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 10,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>Source {i + 1}</span>
                <button
                  type="button"
                  onClick={() => removeSource(src.id)}
                  aria-label={`Remove source ${i + 1}${src.label ? `: ${src.label}` : ''}`}
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}
                >
                  <Icon name="trash" size={13} />
                </button>
              </div>

              <div style={{ marginBottom: 10 }}>
                <label htmlFor={srcLabelId} style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Label / Title</label>
                <input
                  id={srcLabelId}
                  value={src.label}
                  onChange={e => updateSource(src.id, { label: e.target.value })}
                  placeholder="e.g. Wikipedia, New York Times, ISBN 978-…"
                  style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <label htmlFor={srcTextId} style={{ fontSize: 11, color: 'var(--text3)', margin: 0 }}>Content</label>
                  <span
                    id={srcCountId}
                    aria-live="polite"
                    aria-atomic="true"
                    style={{ fontSize: 11, color: efWordCountColor(srcWords, EF_SOURCE_LIMIT) }}
                  >
                    {srcWords.toLocaleString()} / {EF_SOURCE_LIMIT.toLocaleString()} words
                  </span>
                </div>
                <textarea
                  id={srcTextId}
                  value={src.text}
                  onChange={e => updateSource(src.id, { text: e.target.value })}
                  rows={3}
                  placeholder="Quote, excerpt, or notes from this source…"
                  aria-describedby={srcCountId}
                  aria-invalid={srcWords > EF_SOURCE_LIMIT}
                  style={{ resize: 'vertical', overflowWrap: 'break-word', wordBreak: 'break-word', borderColor: srcWords > EF_SOURCE_LIMIT ? 'var(--danger)' : undefined }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Tags */}
      <div style={{ marginBottom: 20 }}>
        <label>Tags <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
        <div style={{ position: 'relative' }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
            background: 'var(--bg3)', border: '1px solid var(--border2)',
            borderRadius: 'var(--radius)', padding: '6px 10px', minHeight: 42,
          }}>
            {tags.map(tag => (
              <span key={tag} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'color-mix(in srgb, var(--accent) 15%, var(--bg3))',
                border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
                borderRadius: 6, padding: '2px 8px', fontSize: 12,
                color: 'var(--accent)', fontWeight: 500,
              }}>
                #{tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove tag ${tag}`}
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 14, opacity: 0.7 }}
                >×</button>
              </span>
            ))}
            <input
              value={tagInput}
              onChange={e => handleTagInputChange(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={() => setTimeout(() => setTagSuggestions([]), 150)}
              placeholder={tags.length === 0 ? 'Add tags… (Enter or comma to confirm)' : ''}
              style={{
                flex: 1, minWidth: 120, background: 'transparent',
                border: 'none', outline: 'none', padding: '2px 0',
                fontSize: 13, color: 'var(--text)',
              }}
            />
          </div>
          {tagSuggestions.length > 0 && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
              background: 'var(--surface)', border: '1px solid var(--border2)',
              borderRadius: 'var(--radius)', boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              overflow: 'hidden',
            }}>
              {tagSuggestions.map(sug => (
                <button
                  key={sug}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); addTag(sug); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    background: 'none', border: 'none', padding: '8px 12px',
                    fontSize: 13, color: 'var(--text)', cursor: 'pointer',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  #{sug}
                </button>
              ))}
            </div>
          )}
        </div>
        <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>
          Tags are normalized to lowercase. Use Enter or comma to add. Backspace removes the last tag.
        </p>
      </div>

      {/* Error */}
      {error && (
        <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          {error}
        </p>
      )}

      {/* Unsaved-changes inline warning */}
      {showLeaveWarning && (
        <div role="alert" style={{
          background: 'rgba(201,88,88,0.1)', border: '1px solid rgba(201,88,88,0.4)',
          borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 12,
        }}>
          <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10, fontWeight: 500 }}>
            You have unsaved changes. Discard them and close?
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn-ghost btn-sm" onClick={() => setShowLeaveWarning(false)}>Keep Editing</button>
            <button
              className="btn-sm"
              onClick={onCancel}
              style={{ background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}
            >
              Discard Changes
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button className="btn-ghost" onClick={handleCancel}>Cancel</button>
        <button className="btn-primary" onClick={save} disabled={descImgUploading} aria-disabled={descImgUploading}>
          {descImgUploading ? 'Uploading…' : 'Save Event'}
        </button>
      </div>
    </div>
  );
};

export default EventForm;
